import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft,
  ExternalLink,
  Copy,
  Zap,
  Gauge,
  Fuel,
  Cpu,
  Layers,
  Radio,
  FileJson,
} from "lucide-react";
import MethodGlyph from "@/components/MethodGlyph";
import { vehicleTypeLabel, fuelLabel } from "@/lib/labels";
import EcuBrandLogo, {
  ecuBrandLogoAvailable,
  ecuBrandFromEcuName,
} from "@/components/EcuBrandLogo";
import VehicleTypeIcon from "@/components/VehicleTypeIcon";

const METHOD_TINT = {
  bench: "sky",
  boot: "fuchsia",
  obd: "emerald",
  jtag: "orange",
  bdm: "rose",
  bsl: "violet",
};

function tint(name) {
  return METHOD_TINT[(name || "").toLowerCase()] || "amber";
}

export default function VehicleDetail() {
  const { cid } = useParams();
  const navigate = useNavigate();
  const [row, setRow] = useState(null);
  const [related, setRelated] = useState({ same_ecu: [], same_manufacturer: [] });
  const [similarFlash, setSimilarFlash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const decoded = decodeURIComponent(cid);
        const { data } = await api.get("/compatibility/by-id", { params: { id: decoded } });
        if (cancelled) return;
        setRow(data);

        // Related
        const rel = await api.get("/compatibility/related", {
          params: {
            id: decoded,
            ecu_name: data.ecu_tcu?.name,
            manufacturer: data.manufacturer,
            limit: 6,
          },
        });
        if (!cancelled) setRelated(rel.data);

        // Similar flash-procedure (cross-manufacturer via shared protocol code)
        const proto = data.ecu_tcu?.protocol?.label;
        if (proto) {
          try {
            const sf = await api.get("/compatibility/similar-flash", {
              params: { protocol: proto, exclude: decoded, limit: 9 },
            });
            if (!cancelled) setSimilarFlash(sf.data);
          } catch (err) {
            console.warn("similar-flash error", err);
          }
        }
      } catch (e) {
        if (e?.response?.status === 404) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cid]);

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("In Zwischenablage kopiert");
    } catch {
      toast.error("Kopieren fehlgeschlagen");
    }
  };

  if (loading) {
    return (
      <div data-testid="detail-loading" className="space-y-4">
        <div className="h-6 w-40 bg-neutral-900 rounded animate-pulse" />
        <div className="h-24 w-2/3 bg-neutral-900 rounded animate-pulse" />
        <div className="grid grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-24 bg-neutral-900 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (notFound || !row) {
    return (
      <div data-testid="detail-not-found" className="text-center py-20">
        <div className="text-neutral-500 text-sm">Eintrag nicht gefunden.</div>
        <Button
          onClick={() => navigate("/")}
          className="mt-4 bg-neutral-800 hover:bg-neutral-700 text-neutral-100"
        >
          Zurück zur Liste
        </Button>
      </div>
    );
  }

  const model = [row.model, row.version].filter(Boolean).join(" ");
  const ecu = row.ecu_tcu || {};

  return (
    <div data-testid="detail-page" className="space-y-10">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-xs text-neutral-500 font-mono">
        <Link
          to="/"
          data-testid="detail-back"
          className="hover:text-[#E13636] inline-flex items-center gap-1"
        >
          <ArrowLeft className="w-3 h-3" /> Compatibility
        </Link>
        <span>/</span>
        <span className="text-neutral-400">{row.manufacturer}</span>
        <span>/</span>
        <span className="text-neutral-200">{model || "—"}</span>
      </div>

      {/* Hero: blueprint style */}
      <section className="relative rounded-2xl border border-neutral-800 bg-panel overflow-hidden">
        {/* Blueprint grid background */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.10]"
          style={{
            backgroundImage:
              "linear-gradient(#D32222 1px, transparent 1px), linear-gradient(90deg, #D32222 1px, transparent 1px)",
            backgroundSize: "40px 40px",
            maskImage:
              "radial-gradient(ellipse at 20% 30%, black 40%, transparent 70%)",
          }}
        />
        <div className="relative px-8 py-10 lg:px-12 lg:py-14 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 items-center">
          <div>
            <div className="flex items-center gap-3 mb-6">
              {row.manufacturer_logo && (
                <img
                  src={row.manufacturer_logo}
                  alt={row.manufacturer}
                  className="h-10 w-10 object-contain rounded bg-neutral-900 border border-neutral-800 p-1.5"
                />
              )}
              <div>
                <div
                  data-testid="detail-manufacturer"
                  className="text-neutral-400 uppercase tracking-[0.3em] text-xs"
                >
                  {row.manufacturer}
                </div>
                <div className="text-[10px] text-neutral-600 font-mono mt-0.5">
                  <VehicleTypeIcon type={row.vehicle_type} className="inline-block h-4 w-5 mr-1 align-[-2px]" />
                  {vehicleTypeLabel(row.vehicle_type)} · {row.year || "—"}
                </div>
              </div>
            </div>
            <h1
              data-testid="detail-model"
              className="text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tight text-neutral-50"
              style={{ fontFamily: "'Orbitron', sans-serif" }}
            >
              {row.model}
              {row.version && (
                <span className="block text-[#D32222] mt-2">{row.version}</span>
              )}
            </h1>
            <div
              data-testid="detail-engine"
              className="mt-6 text-lg text-neutral-300"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              {row.engine || "—"}
            </div>
          </div>

          {/* Spec dial column */}
          <div className="grid grid-cols-2 gap-3">
            <SpecTile
              icon={<Zap className="w-4 h-4" />}
              label="Power"
              value={row.power}
              unit="Hp"
              testid="spec-power"
            />
            <SpecTile
              icon={<Gauge className="w-4 h-4" />}
              label="Torque"
              value={row.torque}
              unit="Nm"
              testid="spec-torque"
            />
            <SpecTile
              icon={<Fuel className="w-4 h-4" />}
              label="Fuel"
              value={fuelLabel(row.fuel)}
              mono
              testid="spec-fuel"
            />
            <SpecTile
              icon={<VehicleTypeIcon type={row.vehicle_type} className="h-5 w-5" />}
              label="Type"
              value={vehicleTypeLabel(row.vehicle_type)}
              mono
              testid="spec-type"
            />
          </div>
        </div>
      </section>

      {/* ECU + Methods */}
      <section className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* ECU/TCU blueprint card */}
        <div
          data-testid="detail-ecu-card"
          className="lg:col-span-3 relative rounded-2xl border border-neutral-800 bg-gradient-to-br from-neutral-950 to-overlay overflow-hidden"
        >
          {/* Chip-like corner */}
          <div className="absolute top-0 right-0 h-40 w-40 pointer-events-none">
            <div className="absolute top-4 right-4 h-32 w-32 border border-[#D32222]/30 rounded-md rotate-[10deg]" />
            <div className="absolute top-8 right-8 h-24 w-24 border border-[#D32222]/20 rounded-md rotate-[5deg]" />
          </div>

          <div className="p-6 lg:p-8 relative">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D32222] mb-3 flex items-center gap-2">
              <Cpu className="w-3 h-3" /> Engine Control Unit
            </div>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div
                  className="text-3xl sm:text-4xl text-neutral-100 leading-tight"
                  style={{ fontFamily: "'Orbitron', sans-serif" }}
                  data-testid="detail-ecu-name"
                >
                  {ecu.name ? (
                    <Link
                      to={`/ecu/${encodeURIComponent(ecu.name)}`}
                      className="hover:text-[#EB4E4E] transition inline-flex items-center gap-2"
                      data-testid="detail-ecu-name-link"
                    >
                      {ecuBrandLogoAvailable(ecuBrandFromEcuName(ecu.name)) && (
                        <span className="h-7 w-11 flex items-center justify-center shrink-0">
                          <EcuBrandLogo
                            name={ecuBrandFromEcuName(ecu.name)}
                            className="max-h-7 max-w-11 w-auto"
                          />
                        </span>
                      )}
                      {ecu.name}
                      <span className="text-neutral-500 text-sm">→</span>
                    </Link>
                  ) : (
                    "—"
                  )}
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {ecu.protocol?.label && (
                    <Badge
                      data-testid="detail-protocol"
                      variant="outline"
                      className="border-[#D32222]/40 text-[#EB4E4E] bg-[#D32222]/10 font-mono text-xs px-2.5 py-1"
                    >
                      <Radio className="w-3 h-3 mr-1.5" /> {ecu.protocol.label}
                    </Badge>
                  )}
                  {ecu.type?.label && (
                    <Badge
                      data-testid="detail-ecu-type"
                      variant="outline"
                      className="border-neutral-700 text-neutral-200 bg-neutral-900 font-mono text-xs px-2.5 py-1"
                    >
                      <Layers className="w-3 h-3 mr-1.5" /> {ecu.type.label}
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              {ecu.url && (
                <a
                  href={ecu.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="detail-ecu-link"
                  className="inline-flex items-center gap-1.5 text-sm text-[#E13636] hover:text-[#EB4E4E] transition"
                >
                  Original auf autotuner.com <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
              {ecu.url && (
                <button
                  data-testid="detail-ecu-copy"
                  onClick={() => copy(ecu.url)}
                  className="inline-flex items-center gap-1.5 text-xs text-neutral-500 hover:text-neutral-200 transition"
                >
                  <Copy className="w-3 h-3" /> URL kopieren
                </button>
              )}
            </div>

            {ecu.detail?.description && (
              <div
                data-testid="detail-ecu-description"
                className="mt-6 pt-6 border-t border-neutral-800/70 text-sm text-neutral-400 leading-relaxed line-clamp-6"
              >
                {ecu.detail.description}
              </div>
            )}
            {!ecu.detail?.description && (
              <div className="mt-6 pt-6 border-t border-neutral-800/70 text-xs text-neutral-600 italic">
                Keine ECU-Beschreibung im Cache. Starte den Details-Spider (
                <Link to="/scrape" className="underline decoration-dotted hover:text-neutral-300">
                  Scraper-Seite
                </Link>
                ), um die Detailseite von autotuner.com zu holen.
              </div>
            )}
          </div>
        </div>

        {/* Methods track */}
        <div
          data-testid="detail-methods-card"
          className="lg:col-span-2 rounded-2xl border border-neutral-800 bg-panel p-6 lg:p-8"
        >
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D32222] mb-4">
            Read / Flash Methods
          </div>
          {(row.methods || []).length === 0 ? (
            <div className="text-sm text-neutral-500">Keine Methoden erfasst.</div>
          ) : (
            <ol className="space-y-3">
              {(row.methods || []).map((m, i) => (
                <MethodItem key={i} m={m} index={i + 1} />
              ))}
            </ol>
          )}
        </div>
      </section>

      {/* Similar Flash Procedure (cross-manufacturer via shared protocol code) */}
      {similarFlash && similarFlash.total_matches > 0 && (
        <section
          data-testid="similar-flash-section"
          className="rounded-2xl border border-neutral-800 bg-panel overflow-hidden"
        >
          <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-0">
            {/* Header column */}
            <div className="p-6 lg:p-8 border-b lg:border-b-0 lg:border-r border-neutral-800 bg-overlay/40 relative overflow-hidden">
              {/* subtle radar-like corner */}
              <div
                aria-hidden
                className="absolute -bottom-16 -right-16 h-56 w-56 rounded-full border border-[#D32222]/20"
              />
              <div
                aria-hidden
                className="absolute -bottom-8 -right-8 h-40 w-40 rounded-full border border-[#D32222]/15"
              />
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D32222] mb-3 flex items-center gap-2">
                <Radio className="w-3 h-3" /> Same Flash Procedure
              </div>
              <div
                data-testid="similar-flash-protocol"
                className="text-4xl text-neutral-100 leading-none"
                style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: "-0.02em" }}
              >
                {similarFlash.protocol}
              </div>
              <div className="mt-6 space-y-3">
                <BigStat
                  value={similarFlash.unique_manufacturers}
                  label="Hersteller nutzen diesen Chip"
                  testid="sf-mfr"
                />
                <BigStat
                  value={similarFlash.unique_ecus}
                  label="ECU-Varianten insgesamt"
                  testid="sf-ecus"
                />
                <BigStat
                  value={similarFlash.total_matches}
                  label="Fahrzeuge betroffen"
                  testid="sf-total"
                />
              </div>
              <div className="mt-6 text-[11px] text-neutral-500 leading-relaxed">
                Der Protokoll-Code definiert die Flash-Prozedur unabhängig
                vom Fahrzeug – dieselbe Hardware-Anbindung funktioniert oft
                markenübergreifend.
              </div>
            </div>

            {/* Cards column */}
            <div className="p-6 lg:p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {similarFlash.items.map((r) => (
                  <SimilarCard key={r.id} row={r} />
                ))}
              </div>
              {similarFlash.unique_manufacturers > similarFlash.items.length && (
                <div className="mt-4 text-xs text-neutral-500">
                  Zeige {similarFlash.items.length} von{" "}
                  <span className="text-neutral-300">
                    {similarFlash.unique_manufacturers}
                  </span>{" "}
                  Herstellern.{" "}
                  <Link
                    to={`/?search=${encodeURIComponent(similarFlash.protocol)}`}
                    className="text-[#D32222] hover:text-[#E13636] underline decoration-dotted"
                    data-testid="similar-flash-see-all"
                  >
                    Alle in der Liste öffnen →
                  </Link>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Related */}
      {(related.same_ecu?.length > 0 || related.same_manufacturer?.length > 0) && (
        <section className="space-y-8">
          {related.same_ecu?.length > 0 && (
            <RelatedBlock
              title={`Weitere Fahrzeuge mit ${ecu.name}`}
              subtitle="Gleiche ECU/TCU"
              items={related.same_ecu}
              testid="related-same-ecu"
            />
          )}
          {related.same_manufacturer?.length > 0 && (
            <RelatedBlock
              title={`Auch von ${row.manufacturer}`}
              subtitle="Gleicher Hersteller"
              items={related.same_manufacturer}
              testid="related-same-manufacturer"
            />
          )}
        </section>
      )}

      {/* Raw JSON */}
      <details className="rounded-xl border border-neutral-800 bg-panel">
        <summary
          data-testid="detail-raw-toggle"
          className="cursor-pointer px-4 py-3 text-xs uppercase tracking-widest text-neutral-500 hover:text-neutral-200 flex items-center gap-2"
        >
          <FileJson className="w-3.5 h-3.5" /> Raw JSON
        </summary>
        <pre
          data-testid="detail-raw-json"
          className="p-4 text-[11px] leading-relaxed text-neutral-400 overflow-auto max-h-[400px]"
          style={{ fontFamily: "'JetBrains Mono', monospace" }}
        >
          {JSON.stringify(row, null, 2)}
        </pre>
      </details>
    </div>
  );
}

function SpecTile({ icon, label, value, unit, mono, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-xl border border-neutral-800 bg-overlay/40 backdrop-blur-sm p-4 min-w-[130px]"
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-neutral-500">
        {icon} {label}
      </div>
      <div className="mt-2 flex items-baseline gap-1">
        <span
          className={`text-2xl text-neutral-100 tabular-nums ${
            mono ? "font-mono" : ""
          }`}
          style={
            mono
              ? { fontFamily: "'JetBrains Mono', monospace" }
              : { fontFamily: "'Orbitron', sans-serif" }
          }
        >
          {value || "—"}
        </span>
        {unit && value && (
          <span className="text-xs text-neutral-500 uppercase tracking-widest">
            {unit}
          </span>
        )}
      </div>
    </div>
  );
}

function MethodItem({ m, index }) {
  const t = tint(m.label);
  const dotClass = {
    sky: "bg-sky-400",
    fuchsia: "bg-fuchsia-400",
    emerald: "bg-emerald-400",
    orange: "bg-orange-400",
    rose: "bg-rose-400",
    violet: "bg-violet-400",
    amber: "bg-amber-400",
  }[t];
  return (
    <li
      data-testid={`method-item-${m.label}`}
      className="relative flex gap-4 items-start group"
    >
      {/* connector line */}
      <div className="flex flex-col items-center pt-1">
        <div className={`h-2.5 w-2.5 rounded-full ${dotClass} ring-4 ring-neutral-950`} />
        <div className="w-px flex-1 bg-neutral-800 mt-1 group-last:hidden" />
      </div>
      <div className="flex-1 pb-4 border-b border-neutral-900 group-last:border-b-0">
        <div className="flex items-center gap-2">
          <span
            className="text-[10px] font-mono text-neutral-600 tabular-nums"
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
          >
            {String(index).padStart(2, "0")}
          </span>
          <a
            href={m.url}
            target="_blank"
            rel="noreferrer"
            title={m.label}
            data-testid={`method-link-${m.label}`}
            className="text-neutral-100 font-mono uppercase tracking-widest text-sm hover:text-[#E13636] transition inline-flex items-center gap-1.5"
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
          >
            <MethodGlyph label={m.label} className="w-10 h-10" />
            <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition" />
          </a>
        </div>
        {m.detail?.description ? (
          <div className="mt-1.5 text-xs text-neutral-500 leading-relaxed line-clamp-3">
            {m.detail.description}
          </div>
        ) : (
          <div className="mt-1.5 text-[11px] text-neutral-600 font-mono truncate">
            {m.url}
          </div>
        )}
      </div>
    </li>
  );
}

function RelatedBlock({ title, subtitle, items, testid }) {
  return (
    <div data-testid={testid}>
      <div className="mb-4 flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D32222]">
            {subtitle}
          </div>
          <h2
            className="text-2xl sm:text-3xl text-neutral-100 mt-1"
            style={{ fontFamily: "'Orbitron', sans-serif" }}
          >
            {title}
          </h2>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((r) => (
          <RelatedCard key={r.id} row={r} />
        ))}
      </div>
    </div>
  );
}

function RelatedCard({ row }) {
  const model = [row.model, row.version].filter(Boolean).join(" ");
  const ecu = row.ecu_tcu || {};
  return (
    <Link
      to={`/vehicle/${encodeURIComponent(row.id)}`}
      data-testid="related-card"
      className="group rounded-xl border border-neutral-800 bg-panel p-4 hover:border-[#D32222]/50 hover:bg-neutral-900/50 transition"
    >
      <div className="flex items-center gap-2">
        {row.manufacturer_logo && (
          <img
            src={row.manufacturer_logo}
            alt=""
            className="h-6 w-6 object-contain rounded bg-neutral-900 border border-neutral-800 p-0.5"
          />
        )}
        <div className="text-xs uppercase tracking-widest text-neutral-500">
          {row.manufacturer}
        </div>
      </div>
      <div className="mt-2 text-neutral-100 group-hover:text-[#EB4E4E] transition">
        {model || "—"}{" "}
        <span className="text-neutral-500 text-xs">· {row.year || "—"}</span>
      </div>
      <div className="mt-1 text-xs text-neutral-500 truncate">
        {row.engine || "—"}
      </div>
      <div
        className="mt-2 text-[10px] text-neutral-500 font-mono truncate"
        style={{ fontFamily: "'JetBrains Mono', monospace" }}
      >
        {ecu.name} · {ecu.protocol?.label} · {ecu.type?.label}
      </div>
    </Link>
  );
}

function SimilarCard({ row }) {
  const model = [row.model, row.version].filter(Boolean).join(" ");
  const ecu = row.ecu_tcu || {};
  return (
    <Link
      to={`/vehicle/${encodeURIComponent(row.id)}`}
      data-testid="similar-flash-card"
      className="group relative rounded-xl border border-neutral-800 bg-overlay/40 p-4 hover:border-[#D32222]/50 transition overflow-hidden"
    >
      <div className="flex items-center gap-2">
        {row.manufacturer_logo ? (
          <img
            src={row.manufacturer_logo}
            alt=""
            className="h-6 w-6 object-contain rounded bg-neutral-900 border border-neutral-800 p-0.5"
          />
        ) : (
          <div className="h-6 w-6 rounded bg-neutral-900 border border-neutral-800" />
        )}
        <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 truncate">
          {row.manufacturer}
        </div>
      </div>
      <div
        className="mt-2 text-neutral-100 group-hover:text-[#EB4E4E] transition"
        style={{ fontFamily: "'Orbitron', sans-serif" }}
      >
        <span className="text-lg">{row.model || "—"}</span>{" "}
        <span className="text-neutral-500 text-xs font-sans">
          · {row.year || "—"}
        </span>
      </div>
      <div
        className="mt-2 text-[10px] text-neutral-500 font-mono truncate"
        style={{ fontFamily: "'JetBrains Mono', monospace" }}
      >
        {ecu.name}
      </div>
      <div className="mt-2 flex flex-wrap gap-1">
        {(row.methods || []).slice(0, 4).map((m, i) => (
          <span
            key={i}
            title={m.label}
            className="inline-flex items-center text-[9px] font-mono uppercase tracking-widest border border-neutral-700 text-neutral-400 rounded px-1.5 py-0.5"
          >
            <MethodGlyph label={m.label} className="w-9 h-9" />
          </span>
        ))}
      </div>
    </Link>
  );
}

function BigStat({ value, label, testid }) {
  return (
    <div data-testid={testid} className="flex items-baseline gap-2">
      <span
        className="text-3xl text-[#D32222] tabular-nums"
        style={{ fontFamily: "'Orbitron', sans-serif" }}
      >
        {value?.toLocaleString?.("de-DE") ?? value}
      </span>
      <span className="text-[11px] text-neutral-400 leading-tight">{label}</span>
    </div>
  );
}
